// Notification bell + dropdown panel.
//
// The in-tab toast is ephemeral (5 s); this panel is the permanent home for
// every alert fireNotification raises — order chats, direct messages, group
// rooms, forwarded SMS, new emails, order-assigned. Miss the toast, still see
// it here. The store lives in services/notificationCenter (localStorage keyed
// per user) so a reload keeps history. See NotificationHandler for the fire-
// side wiring.
//
// One panel, two bells: the mobile header replaces its old messages-shortcut
// bell with <NotificationBell />, and the desktop shell renders another one in
// a fixed top-right position. Both toggle the same panel via a module-scoped
// open-state store, mirroring the notification store's pattern.
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, Trash2, Mail, MessageSquare, Smartphone, Users, Truck, Info } from 'lucide-react';
import {
  useNotificationCenter,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  removeNotification,
  type NotificationItem,
  type NotificationKind,
} from '../services/notificationCenter';

// ── Panel open-state (module singleton, same pattern as the store) ─────────
let panelOpen = false;
const openListeners = new Set<() => void>();
const setPanelOpen = (v: boolean) => {
  if (panelOpen === v) return;
  panelOpen = v;
  for (const l of openListeners) l();
};
const usePanelOpen = (): boolean =>
  useSyncExternalStore(
    (cb) => { openListeners.add(cb); return () => { openListeners.delete(cb); }; },
    () => panelOpen,
    () => false,
  );

// ── Small helpers ──────────────────────────────────────────────────────────
const relativeTime = (ms: number): string => {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 45) return 'just now';
  if (s < 90) return '1 min ago';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(ms).toLocaleDateString();
};

const KindIcon: React.FC<{ kind: NotificationKind; className?: string }> = ({ kind, className }) => {
  const cls = className || 'text-slate-500';
  switch (kind) {
    case 'sms':   return <Smartphone size={16} className={cls} />;
    case 'email': return <Mail size={16} className={cls} />;
    case 'room':  return <Users size={16} className={cls} />;
    case 'order': return <Truck size={16} className={cls} />;
    case 'chat':  return <MessageSquare size={16} className={cls} />;
    default:      return <Info size={16} className={cls} />;
  }
};

// ── Floating bell (desktop) — draggable ──────────────────────────────────────
// The desktop bell floats over the page and used to sit on top of page buttons
// (the Orders header, the board's toolbar). Umer, 2026-10-03: let the user drag it
// where it suits them. The place is kept per browser (localStorage), clamped to
// the viewport on every move and on resize. A press that moves less than the
// drag threshold is a click (opens the panel); a drag never opens it.
const BELL_POS_KEY = 'wd.bell.pos';
const DRAG_THRESHOLD_PX = 4;
const BELL_MARGIN_PX = 8;
type BellPos = { x: number; y: number };

const readBellPos = (): BellPos | null => {
  try {
    const raw = localStorage.getItem(BELL_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return typeof p?.x === 'number' && typeof p?.y === 'number' ? { x: p.x, y: p.y } : null;
  } catch { return null; }
};
const writeBellPos = (p: BellPos) => { try { localStorage.setItem(BELL_POS_KEY, JSON.stringify(p)); } catch { /* private window */ } };
const clampBellPos = (p: BellPos, size: number): BellPos => {
  const maxX = Math.max(BELL_MARGIN_PX, (typeof window !== 'undefined' ? window.innerWidth : 1280) - size - BELL_MARGIN_PX);
  const maxY = Math.max(BELL_MARGIN_PX, (typeof window !== 'undefined' ? window.innerHeight : 800) - size - BELL_MARGIN_PX);
  return { x: Math.min(Math.max(BELL_MARGIN_PX, p.x), maxX), y: Math.min(Math.max(BELL_MARGIN_PX, p.y), maxY) };
};

const BELL_SIZE_PX = 36; // the bell button is w-9 h-9

export const FloatingNotificationBell: React.FC = () => {
  const SIZE = BELL_SIZE_PX;
  // A saved place is clamped on the way in too: a smaller window than last time keeps the bell on screen.
  const [pos, setPos] = useState<BellPos | null>(() => { const p = readBellPos(); return p ? clampBellPos(p, BELL_SIZE_PX) : null; });
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number; moved: boolean; pointerId: number } | null>(null);
  const suppressClickRef = useRef(false);

  // Keep it on screen when the window shrinks.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampBellPos(p, SIZE) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const currentPos = (el: HTMLElement): BellPos => {
    if (pos) return pos;
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top };
  };
  // No pointer capture: with capture the click after a press lands on this wrapper,
  // not on the bell button, and the panel never opens. Move and release are watched
  // on the window instead, for as long as one press lasts.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const origin = currentPos(e.currentTarget);
    const d = { startX: e.clientX, startY: e.clientY, originX: origin.x, originY: origin.y, moved: false, pointerId: e.pointerId };
    dragRef.current = d;
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== d.pointerId) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      d.moved = true;
      setPos(clampBellPos({ x: d.originX + dx, y: d.originY + dy }, SIZE));
    };
    const onEnd = (ev: PointerEvent) => {
      if (ev.pointerId !== d.pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      dragRef.current = null;
      if (d.moved) {
        suppressClickRef.current = true; // the click that follows a drag must not open the panel
        setPos((p) => { if (p) writeBellPos(p); return p; });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  };
  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (suppressClickRef.current) { suppressClickRef.current = false; e.preventDefault(); e.stopPropagation(); }
  };

  const style: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y, right: 'auto' }
    : {};
  return (
    <div
      data-testid="floating-bell"
      className="hidden md:block fixed top-4 right-6 z-40 touch-none select-none cursor-grab active:cursor-grabbing"
      style={style}
      title="Drag to move"
      onPointerDown={onPointerDown}
      onClickCapture={onClickCapture}
    >
      <NotificationBell variant="light" />
    </div>
  );
};

// ── Bell button ────────────────────────────────────────────────────────────
// Placed in the mobile header AND (separately) in a desktop top-right float.
// The `variant` picks colours that read on each background; both bells share
// the module-scoped panel open state, so tapping either opens the same panel.
export const NotificationBell: React.FC<{ variant?: 'dark' | 'light'; className?: string }> = ({
  variant = 'dark',
  className = '',
}) => {
  const open = usePanelOpen();
  const { unread } = useNotificationCenter();

  // ARIA label: the count is announced to screen readers, not just painted on
  // the badge — a bell that only says "Notifications" hides the news itself.
  const label = unread > 0
    ? `Notifications, ${unread} unread`
    : 'Notifications';

  const base =
    'relative inline-flex items-center justify-center rounded-full transition-colors';
  const size = 'w-9 h-9';
  const skin = variant === 'dark'
    ? 'text-white hover:bg-slate-800'
    : 'text-slate-700 bg-white shadow-md border border-slate-200 hover:bg-slate-50';

  return (
    <button
      type="button"
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => setPanelOpen(!open)}
      className={`${base} ${size} ${skin} ${className}`}
    >
      <Bell size={20} />
      {unread > 0 && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-slate-900"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
};

// ── Panel ──────────────────────────────────────────────────────────────────
type Tab = 'all' | 'unread';

export const NotificationPanel: React.FC = () => {
  const open = usePanelOpen();
  const { items, unread } = useNotificationCenter();
  const [tab, setTab] = React.useState<Tab>('all');
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click. The bell button lives OUTSIDE the panel and is the
  // toggle, so its own click must not immediately re-close — the toggle handles
  // that. Any other click outside closes the panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t)) return;
      // A tap on any bell button should still toggle, not close-then-open.
      const bell = (t as HTMLElement).closest?.('[aria-haspopup="dialog"]');
      if (bell) return;
      setPanelOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  // Close on ESC — keyboard users deserve the same escape hatch as tap-away.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPanelOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const visible = tab === 'unread' ? items.filter(i => !i.read) : items;

  const handleRowClick = (item: NotificationItem) => {
    markNotificationRead(item.id);
    if (item.url) navigate(item.url);
    setPanelOpen(false);
  };

  return (
    <>
      {/* Mobile-only backdrop so the panel reads as a sheet on small widths. */}
      <div
        className="fixed inset-0 bg-black/30 z-[9998] md:hidden"
        onClick={() => setPanelOpen(false)}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Notifications"
        className="
          fixed z-[9999]
          inset-x-2 top-16 bottom-4 max-h-[85vh]
          md:inset-auto md:top-16 md:right-6 md:bottom-auto md:w-[380px] md:max-h-[70vh]
          bg-white rounded-2xl shadow-2xl border border-slate-200
          flex flex-col overflow-hidden
        "
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Notifications</h3>
            <p className="text-xs text-slate-500">
              {unread === 0 ? 'All caught up' : `${unread} unread`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPanelOpen(false)}
            aria-label="Close notifications"
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-2 pt-2 flex gap-1 border-b border-slate-100 shrink-0">
          {(['all', 'unread'] as Tab[]).map(t => {
            const active = tab === t;
            const count = t === 'all' ? items.length : unread;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-xs font-bold rounded-t-lg transition-colors ${
                  active
                    ? 'text-brand-700 border-b-2 border-brand-600 bg-brand-50/60'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {t === 'all' ? 'All' : 'Unread'}
                {count > 0 && (
                  <span className={`ml-1.5 text-[10px] font-black ${active ? 'text-brand-700' : 'text-slate-400'}`}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {visible.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              {tab === 'unread' ? 'No unread notifications.' : 'No notifications yet.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map(item => (
                <li key={item.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(item);
                      }
                    }}
                    className={`group px-4 py-3 flex gap-3 items-start cursor-pointer transition-colors ${
                      item.read ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/60 hover:bg-blue-50'
                    }`}
                  >
                    <div className="shrink-0 mt-0.5 relative">
                      <KindIcon kind={item.kind} className={item.read ? 'text-slate-400' : 'text-blue-600'} />
                      {!item.read && (
                        <span
                          aria-hidden
                          className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-blue-500 ring-2 ring-white"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <p className={`text-sm truncate ${item.read ? 'text-slate-700 font-medium' : 'text-slate-900 font-bold'}`}>
                          {item.title}
                        </p>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {relativeTime(item.createdAt)}
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${item.read ? 'text-slate-500' : 'text-slate-700'}`}>
                        {item.body}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeNotification(item.id); }}
                      aria-label="Dismiss this notification"
                      className="shrink-0 text-slate-300 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer actions */}
        {items.length > 0 && (
          <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between shrink-0 bg-slate-50">
            <button
              type="button"
              onClick={() => markAllNotificationsRead()}
              disabled={unread === 0}
              className="text-xs font-bold text-slate-700 hover:text-brand-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Check size={14} /> Mark all read
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Clear all notifications from this device?')) clearAllNotifications();
              }}
              className="text-xs font-bold text-slate-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={14} /> Clear all
            </button>
          </div>
        )}
      </div>
    </>
  );
};
